/* eslint-disable max-lines */
import { Alert, Button, InputField } from '@hakimo-ui/shared/ui-base';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { toast } from '@hakimo-ui/hakimo/util';
import { useState } from 'react';
import {
  PRODUCT_TYPE_OPTIONS,
  validateTenantId,
  validateTenantName,
} from './util';

interface Props {
  mode: 'create' | 'edit';
  tenantId: string;
  tenantName: string;
  sourceSystem: 'Motion' | 'Vision' | undefined;
  supportUserEmail?: string | null;
  onTenantIdChange?: (value: string) => void;
  onTenantNameChange?: (value: string) => void;
  onSourceSystemChange: (value: 'Motion' | 'Vision') => void;
}

export function CreateTenantCard(props: Props) {
  const {
    mode,
    tenantId,
    tenantName,
    sourceSystem,
    supportUserEmail,
    onTenantIdChange,
    onTenantNameChange,
    onSourceSystemChange,
  } = props;

  const [tenantIdError, setTenantIdError] = useState('');
  const [tenantNameError, setTenantNameError] = useState('');

  const computedEmail =
    mode === 'create' && tenantName
      ? `support_${tenantName.toLowerCase().replace(/\s+/g, '_')}@hakimo.ai`
      : null;

  const displayEmail = supportUserEmail || computedEmail || '---';

  const handleTenantIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onTenantIdChange?.(value);
    const validation = validateTenantId(value);
    setTenantIdError(validation.error);
  };

  const handleTenantNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onTenantNameChange?.(value);
    const validation = validateTenantName(value);
    setTenantNameError(validation.error);
  };

  const handleCopy = async () => {
    const emailToCopy = supportUserEmail || computedEmail;
    if (!emailToCopy) {
      toast('No support user email to copy', { type: 'error' });
      return;
    }
    try {
      await navigator.clipboard.writeText(emailToCopy);
      toast('Support user email copied to clipboard', { type: 'success' });
    } catch {
      toast('Failed to copy to clipboard', { type: 'error' });
    }
  };

  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 overflow-hidden rounded-xl">
      <div className="px-6 py-6">
        <div className="space-y-4">
          <div>
            {mode === 'create' ? (
              <InputField
                label="Name"
                type="text"
                value={tenantName}
                onChange={handleTenantNameChange}
                error={!!tenantNameError}
                errorText={tenantNameError}
                placeholder="Enter tenant name"
              />
            ) : (
              <>
                <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                  Name
                </dt>
                <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                  {tenantName}
                </dd>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              {mode === 'create' ? (
                <InputField
                  label="ID"
                  type="text"
                  value={tenantId}
                  onChange={handleTenantIdChange}
                  error={!!tenantIdError}
                  errorText={tenantIdError}
                  placeholder="Enter Tenant ID"
                />
              ) : (
                <>
                  <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                    ID
                  </dt>
                  <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 text-sm font-semibold">
                    {tenantId}
                  </dd>
                </>
              )}
            </div>
            <div>
              <dt className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 text-sm">
                Product Type
              </dt>
              <SelectMenu
                value={PRODUCT_TYPE_OPTIONS.find(
                  (option) => option.id === sourceSystem
                )}
                items={PRODUCT_TYPE_OPTIONS}
                onChange={(option) =>
                  onSourceSystemChange(option.id as 'Motion' | 'Vision')
                }
                displayValue={(option) =>
                  option?.label ?? 'Select Product Type'
                }
                id={(option) => option.id}
                placeholder="Select Product Type"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <dt className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
                  Support User Email
                </dt>
                <dd className="text-onlight-text-1 dark:text-ondark-text-1 mt-1 pb-2 text-sm font-semibold">
                  {displayEmail}
                </dd>
              </div>
              {mode === 'edit' && supportUserEmail && (
                <Button
                  variant="icon"
                  onClick={handleCopy}
                  title="Copy to clipboard"
                >
                  <DocumentDuplicateIcon className="h-5 w-5" />
                </Button>
              )}
            </div>
            {(supportUserEmail || computedEmail) && (
              <Alert type="info" size="small" className="mt-2">
                Password for this support user email{' '}
                {mode === 'create'
                  ? 'will be auto-generated and available in 1Pass'
                  : 'is available in 1Pass'}
                .
              </Alert>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateTenantCard;
